import { useState, useEffect } from 'react';
import { getOrdini, getOrdineDetail, deleteOrdine } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, ShoppingCart, Euro, Eye, CreditCard, Wallet, Building2, User, QrCode, ExternalLink, CheckCircle, Clock, XCircle, Trash2 } from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';

const OrdiniPage = () => {
  const [ordini, setOrdini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedOrdine, setSelectedOrdine] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ordineToDelete, setOrdineToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadOrdini();
  }, []);

  const loadOrdini = async () => {
    try {
      const response = await getOrdini();
      setOrdini(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento ordini');
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = async (ordineId) => {
    setDetailLoading(true);
    setDetailDialogOpen(true);
    try {
      const response = await getOrdineDetail(ordineId);
      setSelectedOrdine(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dettaglio');
      setDetailDialogOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openDeleteDialog = (ordine) => {
    if (ordine.stato === 'paid' || ordine.stato === 'pagato') {
      toast.error('Non puoi eliminare un ordine già pagato');
      return;
    }
    setOrdineToDelete(ordine);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!ordineToDelete) return;
    setDeleting(true);
    try {
      await deleteOrdine(ordineToDelete.id);
      toast.success('Ordine eliminato');
      loadOrdini();
      setDeleteDialogOpen(false);
      setOrdineToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const getStatoBadge = (stato) => {
    switch(stato) {
      case 'paid':
      case 'pagato':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Pagato</Badge>;
      case 'pending':
      case 'in_attesa':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />In attesa</Badge>;
      case 'cancelled':
      case 'annullato':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Annullato</Badge>;
      default:
        return <Badge variant="secondary">{stato || 'N/D'}</Badge>;
    }
  };

  const getMetodoPagamentoBadge = (metodo) => {
    switch(metodo) {
      case 'stripe':
        return <Badge className="bg-purple-100 text-purple-700"><CreditCard className="h-3 w-3 mr-1" />Stripe</Badge>;
      case 'paypal':
        return <Badge className="bg-blue-100 text-blue-700"><Wallet className="h-3 w-3 mr-1" />PayPal</Badge>;
      case 'manuale':
        return <Badge className="bg-slate-100 text-slate-700">Manuale</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  const filteredOrdini = ordini.filter(o => 
    o.id?.toLowerCase().includes(search.toLowerCase()) ||
    o.ragione_sociale?.toLowerCase().includes(search.toLowerCase()) ||
    o.nome_elogio?.toLowerCase().includes(search.toLowerCase()) ||
    o.stato?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="ordini-page">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 
          className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Ordini
        </h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Storico pagamenti e attivazioni QR</p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca per ID, azienda, elogio o stato..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-ordini"
          />
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredOrdini.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <ShoppingCart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessun ordine presente</p>
            </CardContent>
          </Card>
        ) : (
          filteredOrdini.map((ordine) => (
            <Card key={ordine.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono text-sm font-medium">{ordine.id}</p>
                    <p className="text-sm text-slate-500">{ordine.ragione_sociale || ordine.id_azienda}</p>
                  </div>
                  {getStatoBadge(ordine.stato)}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Memorial:</span>
                    <span className="font-medium">{ordine.nome_elogio || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Importo:</span>
                    <span className="font-semibold">€{ordine.importo?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Metodo:</span>
                    {getMetodoPagamentoBadge(ordine.metodo_pagamento)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Data:</span>
                    <span>{ordine.data_creazione ? new Date(ordine.data_creazione).toLocaleDateString('it-IT') : '-'}</span>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => viewDetail(ordine.id)}
                    data-testid={`view-detail-${ordine.id}`}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Dettagli
                  </Button>
                  {ordine.stato !== 'paid' && ordine.stato !== 'pagato' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => openDeleteDialog(ordine)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          {filteredOrdini.length === 0 ? (
            <div className="p-8 text-center">
              <ShoppingCart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessun ordine presente</p>
              <p className="text-sm text-slate-400 mt-1">Gli ordini appariranno qui quando verranno effettuati</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="ordini-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">ID Ordine</th>
                    <th className="text-left p-4 font-medium text-slate-600">Azienda</th>
                    <th className="text-left p-4 font-medium text-slate-600">Memorial</th>
                    <th className="text-center p-4 font-medium text-slate-600">Importo</th>
                    <th className="text-center p-4 font-medium text-slate-600">Metodo</th>
                    <th className="text-center p-4 font-medium text-slate-600">Stato</th>
                    <th className="text-center p-4 font-medium text-slate-600">Data</th>
                    <th className="text-center p-4 font-medium text-slate-600">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrdini.map((ordine) => (
                    <tr 
                      key={ordine.id} 
                      className="border-b hover:bg-slate-50 transition-colors"
                      data-testid={`ordine-row-${ordine.id}`}
                    >
                      <td className="p-4">
                        <span className="font-mono text-sm">{ordine.id}</span>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{ordine.ragione_sociale || '-'}</p>
                          <p className="text-xs text-slate-500">{ordine.id_azienda}</p>
                        </div>
                      </td>
                      <td className="p-4 text-slate-600">
                        {ordine.nome_elogio || '-'}
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1 font-semibold">
                          <Euro className="h-4 w-4 text-slate-400" />
                          {ordine.importo?.toFixed(2)}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        {getMetodoPagamentoBadge(ordine.metodo_pagamento)}
                      </td>
                      <td className="p-4 text-center">
                        {getStatoBadge(ordine.stato)}
                      </td>
                      <td className="p-4 text-center text-slate-500 text-sm">
                        {ordine.data_creazione ? new Date(ordine.data_creazione).toLocaleDateString('it-IT') : '-'}
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => viewDetail(ordine.id)}
                            data-testid={`view-detail-${ordine.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Dettagli
                          </Button>
                          {ordine.stato !== 'paid' && ordine.stato !== 'pagato' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openDeleteDialog(ordine)}
                              data-testid={`delete-ordine-${ordine.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Dettaglio Ordine */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Dettaglio Ordine
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
          ) : selectedOrdine && (
            <div className="space-y-6">
              {/* Header con stato */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-mono text-lg font-semibold">{selectedOrdine.id}</p>
                  <p className="text-sm text-slate-500">
                    {selectedOrdine.data_creazione && new Date(selectedOrdine.data_creazione).toLocaleString('it-IT')}
                  </p>
                </div>
                {getStatoBadge(selectedOrdine.stato)}
              </div>

              {/* Importo */}
              <div className="text-center py-4 border-b">
                <p className="text-sm text-slate-500">Importo Totale</p>
                <p className="text-3xl font-bold text-slate-900">€{selectedOrdine.importo?.toFixed(2)}</p>
              </div>

              {/* Sezione Azienda */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Azienda
                </h3>
                <div className="bg-slate-50 p-3 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ragione Sociale:</span>
                    <span className="font-medium">{selectedOrdine.ragione_sociale || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">ID Azienda:</span>
                    <span className="font-mono">{selectedOrdine.id_azienda}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Email:</span>
                    <span>{selectedOrdine.email_azienda || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tipo Account:</span>
                    <Badge variant="outline" className="capitalize">{selectedOrdine.tipo_account || 'base'}</Badge>
                  </div>
                </div>
              </div>

              {/* Sezione Memorial */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Memorial
                </h3>
                <div className="bg-slate-50 p-3 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Nome:</span>
                    <span className="font-medium">{selectedOrdine.nome_elogio || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">ID Elogio:</span>
                    <span className="font-mono">{selectedOrdine.id_elogio || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Sezione Pagamento */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Dettagli Pagamento
                </h3>
                <div className="bg-slate-50 p-3 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Metodo:</span>
                    {getMetodoPagamentoBadge(selectedOrdine.metodo_pagamento)}
                  </div>
                  {selectedOrdine.data_pagamento && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Data Pagamento:</span>
                      <span>{new Date(selectedOrdine.data_pagamento).toLocaleString('it-IT')}</span>
                    </div>
                  )}
                  
                  {/* ID Transazione PayPal */}
                  {selectedOrdine.paypal_order_id && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">PayPal Order ID:</span>
                      <span className="font-mono text-xs">{selectedOrdine.paypal_order_id}</span>
                    </div>
                  )}
                  {selectedOrdine.paypal_capture_id && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">PayPal Capture ID:</span>
                      <span className="font-mono text-xs">{selectedOrdine.paypal_capture_id}</span>
                    </div>
                  )}
                  
                  {/* ID Transazione Stripe */}
                  {selectedOrdine.stripe_session_id && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Stripe Session ID:</span>
                      <span className="font-mono text-xs">{selectedOrdine.stripe_session_id}</span>
                    </div>
                  )}
                  {selectedOrdine.stripe_payment_intent && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Stripe Payment Intent:</span>
                      <span className="font-mono text-xs">{selectedOrdine.stripe_payment_intent}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Sezione QR Code */}
              {selectedOrdine.id_link && (
                <div className="space-y-3">
                  <h3 className="font-medium flex items-center gap-2">
                    <QrCode className="h-4 w-4" />
                    QR Code
                  </h3>
                  <div className="bg-slate-50 p-3 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">ID QR:</span>
                      <span className="font-mono">#{selectedOrdine.id_link}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Stato:</span>
                      {selectedOrdine.qr_attivo ? (
                        <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                      ) : (
                        <Badge variant="secondary">Non attivo</Badge>
                      )}
                    </div>
                    {selectedOrdine.qr_link && (
                      <div className="pt-2">
                        <a 
                          href={selectedOrdine.qr_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sky-600 hover:text-sky-700 flex items-center gap-1 text-sm"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {selectedOrdine.qr_link}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Elimina Ordine"
        description="Eliminando questo ordine in attesa, dovrai crearne uno nuovo per procedere con il pagamento."
        itemName={ordineToDelete ? `Ordine ${ordineToDelete.id} - ${ordineToDelete.nome_elogio || 'Memorial'}` : ''}
        loading={deleting}
      />
    </div>
  );
};

export default OrdiniPage;
